import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { Auth, User } from '@angular/fire/auth';
import {
  CalendarService,
  ChannelService,
  PostService,
  ThemeService,
  WorkspaceService,
} from '../services';
import { EMPTY, Observable, of } from 'rxjs';
import { switchMap, tap, catchError, take } from 'rxjs/operators';
import { Workspace } from '../models';
import { AccountService, ProfileService } from '../api';

const DASHBOARD_ROUTE = '/dashboard';

export const workspacesResolver: ResolveFn<Workspace[]> = (route) => {
  const services = {
    workspace: inject(WorkspaceService),
    router: inject(Router),
    auth: inject(Auth),
    profile: inject(ProfileService),
    account: inject(AccountService),
    calendar: inject(CalendarService),
    channels: inject(ChannelService),
    post: inject(PostService),
    theme: inject(ThemeService),
  };

  const workspaceId = route.params['workspaceId'];
  const accountId = route.params['accountId'];
  const user = services.auth.currentUser;

  if (!user || !accountId) {
    services.router.navigate([DASHBOARD_ROUTE]);
    return EMPTY;
  }

  return initializeUserWorkspaces(user, workspaceId, accountId, services);
};

function initializeUserWorkspaces(
  user: User,
  workspaceId: string | undefined,
  accountId: string,
  services: {
    workspace: WorkspaceService;
    router: Router;
    profile: ProfileService;
    account: AccountService;
    calendar: CalendarService;
    channels: ChannelService;
    post: PostService;
    theme: ThemeService;
  },
): Observable<Workspace[]> {
  const currentProfile = services.profile.getProfile();

  if (currentProfile.id && accountId) {
    return services.workspace.getWorkspacesByAccountId(accountId).pipe(
      tap((workspaces) =>
        handleWorkspaceInitialization(workspaces, workspaceId, services),
      ),
      catchError((error) => {
        console.error('Error in workspaces resolver:', error);
        services.router.navigate([DASHBOARD_ROUTE]);
        return of([]);
      }),
    );
  }

  return services.profile.getOrCreateProfile(user).pipe(
    tap((profile) => {
      services.profile.setProfile(profile);
      services.profile.setProfile$(profile);
      // Initialize theme from profile data
      if (profile?.settings?.theme) {
        services.theme.setThemeFromProfile(profile.settings.theme);
      } else {
        services.theme.setThemeFromSystemPreference();
      }
    }),
    switchMap((profile) =>
      services.account.getAccountsByProfileId(profile.id).pipe(
        switchMap((accounts) => {
          services.account.accounts.set(accounts);
          const activeAccount = services.account.findAccountById(accountId);
          if (activeAccount) {
            if (
              activeAccount.active &&
              activeAccount.plan.status === 'cancelled'
            ) {
              services.router.navigate([
                '/dashboard/account',
                accountId,
                'plans',
              ]);
              return of([]);
            }

            services.account.activeAccount.set(activeAccount);
          }

          return activeAccount
            ? services.workspace.getWorkspacesByAccountId(activeAccount.id)
            : of([]);
        }),
        tap((workspaces) => {
          handleWorkspaceInitialization(workspaces, workspaceId, services);
        }),
        catchError((error) => {
          console.error('Error in workspaces resolver:', error);
          services.router.navigate([DASHBOARD_ROUTE]);
          return of([]);
        }),
      ),
    ),
  );
}

function handleWorkspaceInitialization(
  workspaces: Workspace[],
  workspaceId: string | undefined,
  services: {
    workspace: WorkspaceService;
    router: Router;
    calendar: CalendarService;
    channels: ChannelService;
    post: PostService;
  },
): void {
  if (!workspaceId) return;

  const activeWorkspace = workspaces.find(
    (workspace) => workspace.id === workspaceId,
  );

  if (activeWorkspace) {
    services.workspace.activeWorkspace.set(activeWorkspace);
    services.post.doddles.set([]);
    services.post.datelessDoddles.set([]);
    services.calendar.loadDoddlesForCurrentDateRange();
    services.channels
      .getChannels(activeWorkspace.id)
      .pipe(take(1))
      .subscribe({
        error: (error) => console.error('Error loading channels:', error),
      });
  } else {
    services.router.navigate([DASHBOARD_ROUTE]);
  }
}
